// @ts-check

// Distinctive delimiter pair used to mark where a diff hunk should land in
// the RAW MARKDOWN SOURCE, before tiptap-markdown parses it. This matters
// because the agent's old_string/new_string are generated against that raw
// markdown string — if a match is instead searched for in the already-parsed
// ProseMirror document, any match that crossed a markdown syntax boundary
// (e.g. "**bold**" becomes a bold mark and the literal asterisks are gone)
// can never be found. Splicing markers into the source text before parsing
// keeps the search and the parse on the same representation.
//
// "@" and "{"/"}" are deliberately picked from markdown-it's own hardcoded
// terminator-char set (see markdown-it/lib/rules_inline/text.js —
// its comment literally says "{}$%@~+=: reserved for extensions"). The
// built-in `text` rule only stops consuming plain characters when it hits one
// of those; an arbitrary Unicode bracket pair (tried first) does NOT stop it,
// so `text` silently swallows straight through it and our rule never gets a
// chance to fire. The payload itself is encodeURIComponent-escaped, which
// always escapes "@", "{", and "}", so a literal close/open sequence can never
// appear inside it and prematurely end the marker.
export const DIFF_MARKER_OPEN = "@{"
export const DIFF_MARKER_CLOSE = "}@"

/**
 * @typedef {{ id: string, isFullRewrite?: boolean, old_string?: string, new_string: string, rationale?: string, status: string }} CanvasDiff
 */

/**
 * The diffHunk node view forces block-level width, which breaks a markdown
 * table's row/column layout if a diff lands inside one of its cells — skip
 * matches on lines that look like a markdown table row/separator.
 * @param {string} content
 * @param {number} index
 */
function isWithinMarkdownTableRow(content, index) {
  const lineStart = content.lastIndexOf("\n", index) + 1
  const lineEndIdx = content.indexOf("\n", index)
  const line = content.slice(lineStart, lineEndIdx === -1 ? content.length : lineEndIdx)
  return line.trim().startsWith("|")
}

/**
 * Where each diff can be shown in the raw markdown, in document order. A diff
 * is left out when it is a full rewrite, its old_string is not found (already
 * applied, or the agent's quote is not verbatim), it sits in a table row, or
 * it overlaps a diff before it. Those can only be acted on from the list.
 * @param {string} content
 * @param {CanvasDiff[]} pendingDiffs
 */
export function findDiffMatches(content, pendingDiffs) {
  if (!content || !pendingDiffs?.length) return []

  const matches = []
  for (const diff of pendingDiffs) {
    if (diff.isFullRewrite || !diff.old_string) continue
    const index = content.indexOf(diff.old_string)
    if (index === -1 || isWithinMarkdownTableRow(content, index)) continue
    matches.push({ index, length: diff.old_string.length, diff })
  }
  matches.sort((a, b) => a.index - b.index)

  const placed = []
  let end = 0
  for (const match of matches) {
    if (match.index < end) continue
    placed.push(match)
    end = match.index + match.length
  }
  return placed
}

/**
 * Splice diff markers into a raw markdown string at each diff's old_string
 * location, searched for in that SAME raw string (not the parsed doc).
 * @param {string} content
 * @param {CanvasDiff[]} pendingDiffs
 */
export function injectDiffMarkers(content, pendingDiffs) {
  if (!content || !pendingDiffs?.length) return content ?? ""

  // Splice from the end first so earlier match offsets stay valid.
  let result = content
  for (const { index, length, diff } of findDiffMatches(content, pendingDiffs).reverse()) {
    const payload = encodeURIComponent(JSON.stringify({
      id: diff.id,
      old_string: diff.old_string,
      new_string: diff.new_string,
      rationale: diff.rationale ?? "",
      status: diff.status,
    }))
    result = result.slice(0, index) + DIFF_MARKER_OPEN + payload + DIFF_MARKER_CLOSE + result.slice(index + length)
  }
  return result
}
